"use client";

import * as React from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { XIcon } from "@phosphor-icons/react";

import { cn } from "../../lib/utils";
import { resolveRender } from "../../lib/resolve-render";
import { layerStyle } from "../../lib/layer-style";

/**
 * Migrated from `@radix-ui/react-dialog` to Base UI, together with `sheet.tsx`
 * — both imported the same package, so the dependency only leaves when both do.
 *
 * 286 files consume this and none of them changes. Dialog is the first
 * component in the burndown that is a **container** for ones already migrated,
 * so the risk is not in its own props; it is in what Popover, Select and
 * Autocomplete do *because* their container used to be Radix.
 *
 * `dialog.tsx` was not pristine — it carries SUPP-198 (#5458), and three things
 * from that fix have to survive:
 *
 *  1. **`useIsInsideDialog()`.** `Autocomplete` reads it to decide whether its
 *     Popover needs `modal`. If the provider stops being rendered the hook
 *     still exists, still type-checks, and quietly returns `false` — at which
 *     point three sales-order dropdowns stop scrolling with the wheel. Kept
 *     verbatim, and now asserted.
 *  2. **The scroll lock must keep letting the wheel through** to a list a
 *     Popover portalled out of the dialog's subtree.
 *  3. **`z-[1400]`** on backdrop and content, under the 1500 popper layer with
 *     nested modals threaded between at 1450. Written as an inline style rather
 *     than a bracketed class, because a class like `z-[1400]` on a portalled
 *     ancestor broke Handsontable's selector building in #6211.
 *
 * Part renames: Radix's `Overlay` is Base UI's `Backdrop`, and `Content` is
 * `Popup`. State attributes become `data-[open]` and `data-[ending-style]`.
 */

/** The dialog layer, under the 1500 popper layer. "TODO remove once MUI is gone". */
const DIALOG_Z_INDEX = 1400;

const InsideDialogContext = React.createContext(false);

/**
 * True anywhere inside a `DialogContent` subtree.
 *
 * A modal dialog locks page scrolling, and the only node exempt from that lock
 * is the dialog's own content element. Anything portaled out of that subtree —
 * a non-modal `Popover`, for instance — therefore has its wheel events
 * swallowed, so its list can only be moved by dragging the scrollbar.
 * Components that portal a scrollable list out of the dialog read this to turn
 * their own modal handling on automatically.
 *
 * The wording is now library-neutral: the hazard predates Base UI and outlives
 * Radix, and `Autocomplete` only ever needed the answer, not the mechanism.
 */
function useIsInsideDialog(): boolean {
  return React.useContext(InsideDialogContext);
}

/**
 * The dismissal reasons a caller can block.
 *
 * Radix expressed this as `onEscapeKeyDown` / `onInteractOutside` on the
 * *content*, each handed a DOM event to `preventDefault()`. Base UI reports the
 * reason on the *root's* change event and cancels through `eventDetails`, so
 * the guard cannot stay where it was.
 *
 * Rather than have 14 call sites each hand-roll an `onOpenChange` wrapper — or
 * shim a fake Radix event, which would look right and behave differently —
 * the two intents get names. `Modal.tsx` already used exactly this vocabulary
 * for its own props, so this is the app's existing language, not a new one.
 */
const ESCAPE_REASON = "escape-key";
const OUTSIDE_REASONS = ["outside-press", "focus-out"] as const;

type DialogProps = Omit<DialogPrimitive.Root.Props, "children"> & {
  /** Keep the dialog open when Escape is pressed. Radix: `onEscapeKeyDown` + `preventDefault`. */
  disableEscapeKeyClose?: boolean;
  /** Keep the dialog open on an outside click. Radix: `onInteractOutside` / `onPointerDownOutside` + `preventDefault`. */
  disableBackdropClickClose?: boolean;
  /**
   * Narrowed from Base UI's, which also accepts a render function receiving the
   * active trigger's `payload`. That form belongs to Base UI's `handle` API,
   * which has no Radix equivalent and therefore no caller here — and leaving it
   * in the union widens `children` for all 286 consumers, several of which pass
   * it straight through to something typed as `ReactNode`.
   */
  children?: React.ReactNode;
};

function Dialog({
  disableEscapeKeyClose = false,
  disableBackdropClickClose = false,
  onOpenChange,
  ...props
}: DialogProps) {
  const handleOpenChange: DialogPrimitive.Root.Props["onOpenChange"] = (
    open,
    eventDetails,
  ) => {
    const blocked =
      (disableEscapeKeyClose && eventDetails.reason === ESCAPE_REASON) ||
      (disableBackdropClickClose &&
        (OUTSIDE_REASONS as readonly string[]).includes(eventDetails.reason));

    if (blocked) {
      eventDetails.cancel();
      return;
    }
    onOpenChange?.(open, eventDetails);
  };

  return <DialogPrimitive.Root onOpenChange={handleOpenChange} {...props} />;
}

type DialogTriggerProps = Omit<DialogPrimitive.Trigger.Props, "className"> & {
  className?: string;
  asChild?: boolean;
};

function DialogTrigger({
  asChild = false,
  render,
  children,
  ...props
}: DialogTriggerProps) {
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <DialogPrimitive.Trigger
      data-slot="dialog-trigger"
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </DialogPrimitive.Trigger>
  );
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

type DialogCloseProps = Omit<DialogPrimitive.Close.Props, "className"> & {
  className?: string;
  asChild?: boolean;
};

function DialogClose({
  className,
  asChild = false,
  render,
  children,
  ...props
}: DialogCloseProps) {
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <DialogPrimitive.Close
      data-slot="dialog-close"
      className={cn("cursor-pointer", className)}
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </DialogPrimitive.Close>
  );
}

type DialogOverlayProps = Omit<DialogPrimitive.Backdrop.Props, "className"> & {
  className?: string;
};

/** Radix's `Overlay`. Kept under the old name — 286 files import it. */
function DialogOverlay({ className, ...props }: DialogOverlayProps) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      // Base UI suppresses a *nested* dialog's backdrop by default
      // (`enabled: forceRender || !nested` in `DialogBackdrop`). Radix had no
      // such rule and always rendered one, and `Modal`'s `nested` mode depends
      // on it: the nested overlay is what covers the parent modal's content
      // (SUPP-198 #2). Without this the inner dialog floats over a fully
      // legible parent, which reads as a z-index bug rather than a missing
      // element. Harmless when not nested — the flag only ORs into the same
      // condition.
      forceRender
      style={layerStyle(DIALOG_Z_INDEX, className)}
      className={cn(
        "data-[open]:animate-in data-[ending-style]:animate-out data-[ending-style]:fade-out-0 data-[open]:fade-in-0 fixed inset-0 bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

type DialogContentProps = Omit<DialogPrimitive.Popup.Props, "className"> & {
  className?: string;
  showCloseButton?: boolean;
};

function DialogContent({
  className,
  children,
  showCloseButton = true,
  style,
  ...props
}: DialogContentProps) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        // Keep the dialog layer when callers add inline sizing or layout.
        // Spreading `props` after a standalone default style replaced the
        // entire object, putting the popup behind its own backdrop.
        style={{ ...layerStyle(DIALOG_Z_INDEX, className), ...style }}
        className={cn(
          "bg-background data-[open]:animate-in data-[ending-style]:animate-out data-[ending-style]:fade-out-0 data-[open]:fade-in-0 data-[ending-style]:zoom-out-95 data-[open]:zoom-in-95 fixed top-[50%] left-[50%] grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg",
          className,
        )}
        {...props}
      >
        <InsideDialogContext.Provider value={true}>
          {children}
        </InsideDialogContext.Provider>
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="ring-offset-background focus:ring-ring data-[open]:bg-accent data-[open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden cursor-pointer disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

type DialogTitleProps = Omit<DialogPrimitive.Title.Props, "className"> & {
  className?: string;
  asChild?: boolean;
};

function DialogTitle({
  className,
  asChild = false,
  render,
  children,
  ...props
}: DialogTitleProps) {
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </DialogPrimitive.Title>
  );
}

type DialogDescriptionProps = Omit<
  DialogPrimitive.Description.Props,
  "className"
> & {
  className?: string;
  asChild?: boolean;
};

function DialogDescription({
  className,
  asChild = false,
  render,
  children,
  ...props
}: DialogDescriptionProps) {
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </DialogPrimitive.Description>
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  useIsInsideDialog,
};
